import EmptyState from "../components/ds/EmptyState";
import { Spinner } from "../components/ds/icons";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "../context/TranslationContext";
import { trackAffiliateClick } from "../api/affiliate";
import { getVisitorId, storeReferral } from "../utils/referral";

/**
 * Landing page for affiliate links: /r/:code
 *
 * Counts the click, remembers the code for the program's attribution window, then forwards
 * the visitor to the mint space it points at. Public — most visitors arrive logged out.
 */
const ReferralLandingPage = ({ isDark = true }) => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  // StrictMode double-invokes effects in dev; without this the click fires twice.
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!code || trackedRef.current) return;
    trackedRef.current = true;

    let active = true;
    (async () => {
      try {
        const res = await trackAffiliateClick(code, getVisitorId());
        if (!active) return;
        if (res?.success && res.data?.mintSpaceId) {
          const { mintSpaceId, productId } = res.data;
          storeReferral(res.data.code || code, {
            mintSpaceId,
            productId,
            windowDays: res.data.cookieWindowDays,
          });
          // A code points at one product, so land on that product's page rather than the
          // space — it's the page with the buy button the affiliate is being paid for.
          navigate(
            productId
              ? `/mint-space/${mintSpaceId}/product/${productId}`
              : `/mint-space/${mintSpaceId}`,
            { replace: true }
          );
          return;
        }
        setFailed(true);
      } catch {
        if (active) setFailed(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [code, navigate]);

  return (
    <div
      data-theme={isDark ? "dark" : "light"}
      className="flex min-h-screen w-full flex-col items-center justify-center bg-bg-base px-space-24 font-ds text-label-primary"
    >
      {failed ? (
        <EmptyState
          icon="link"
          title={t("affiliate.linkInactive")}
          message={t("affiliate.linkInactiveHint")}
          actionLabel={t("navigation.discover")}
          onAction={() => navigate("/", { replace: true })}
          className="max-w-[400px]"
        />
      ) : (
        <div className="flex flex-col items-center gap-space-12 text-center">
          <Spinner size={28} />
          <p className="text-subheadline text-label-secondary">{t("affiliate.redirecting")}</p>
        </div>
      )}
    </div>
  );
};

export default ReferralLandingPage;
